import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseAdminListControlsOptions {
  defaultColumnIds: string[]
  defaultPageSize?: number
  searchDebounceMs?: number
}

/**
 * 后台列表页的轻量状态底座。
 *
 * 这里只统一机械性的列表控制：分页、搜索防抖和列显示。筛选项、排序、queryKey
 * 仍留在具体 feature 内，避免把业务差异塞进一个难维护的“万能列表”抽象。
 */
export function useAdminListControls({
  defaultColumnIds,
  defaultPageSize = 10,
  searchDebounceMs = 250,
}: UseAdminListControlsOptions) {
  const [page, setPage] = useState(1)
  const [pageSizeValue, setPageSizeValue] = useState(defaultPageSize)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(() => [...defaultColumnIds])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, searchDebounceMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [searchDebounceMs, searchInput])

  const setPageSize = useCallback((nextPageSize: number) => {
    setPageSizeValue(nextPageSize)
    setPage(1)
  }, [])

  const visibleColumnIdSet = useMemo(() => new Set(visibleColumnIds), [visibleColumnIds])

  return {
    page,
    pageSize: pageSizeValue,
    search,
    searchInput,
    setPage,
    setPageSize,
    setSearchInput,
    setVisibleColumnIds,
    visibleColumnIds,
    visibleColumnIdSet,
  }
}
